import {
  nonEmptyTrimmedStringSchema,
  parseSearchParamOrNull,
} from '@/utils/boundarySchema';

const ANONYMOUS_LAUNCH_DARKLY_KEY_STORAGE_KEY = 'userId';
let ephemeralKey: string | null = null;

/**
 * Anonymous LaunchDarkly context key shared by the Pages Router and `/book`.
 * Both routers must bucket the same browser identically so a flag evaluated
 * on `/` agrees with the same flag re-evaluated on `/book`.
 * Best-effort stable via localStorage; ephemeral when storage is unavailable.
 */
export function getOrCreateAnonymousLaunchDarklyKey(): string {
  if (typeof window === 'undefined') return '';

  try {
    const existing = parseSearchParamOrNull(
      window.localStorage.getItem(ANONYMOUS_LAUNCH_DARKLY_KEY_STORAGE_KEY),
      nonEmptyTrimmedStringSchema,
    );
    if (existing) return existing;

    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    const key = `user_${(array[0] ?? 0).toString(36)}${Date.now().toString(36)}`;
    window.localStorage.setItem(ANONYMOUS_LAUNCH_DARKLY_KEY_STORAGE_KEY, key);
    return key;
  } catch {
    if (!ephemeralKey) {
      ephemeralKey = `user_${Date.now().toString(36)}${Math.floor(
        Math.random() * 1e9,
      ).toString(36)}`;
    }
    return ephemeralKey;
  }
}

/**
 * Proves the key LaunchDarkly evaluated under will survive a hard navigation
 * to `/book`, so both routers bucket the browser identically.
 */
export function isAnonymousLaunchDarklyKeyPersisted(
  activeKey: string | null,
): boolean {
  if (typeof window === 'undefined' || !activeKey) return false;

  try {
    const persistedKey = parseSearchParamOrNull(
      window.localStorage.getItem(ANONYMOUS_LAUNCH_DARKLY_KEY_STORAGE_KEY),
      nonEmptyTrimmedStringSchema,
    );
    return persistedKey === activeKey;
  } catch {
    return false;
  }
}
